import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';

import { db } from '@/firebase-config';
import type { ActivityLogEntry, ActivityLogFilters } from '@/features/activity-log/types';
import { buildProjectSubcollectionPath, type ProjectScope } from '@/features/sync/project-scope';

import type { ActivityLogClient } from '../contracts/activity-log-client';

type ActivityLogDocument = {
  id: string;
  data(): unknown;
};

type ActivityLogSnapshot = {
  docs: ActivityLogDocument[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toActivityLogEntry(docSnap: ActivityLogDocument): ActivityLogEntry {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...(isRecord(data) ? data : {}),
  } as ActivityLogEntry;
}

function entriesFromSnapshot(snapshot: ActivityLogSnapshot): ActivityLogEntry[] {
  return snapshot.docs.map(toActivityLogEntry);
}

export class FirebaseActivityLogClient implements ActivityLogClient {
  async getActivityLog(scope: ProjectScope, filters: ActivityLogFilters = {}): Promise<ActivityLogEntry[]> {
    let activityQuery = query(
      collection(db, buildProjectSubcollectionPath(scope, 'activityLog')),
      orderBy('timestamp', 'desc'),
    );

    if (filters.entityType) {
      activityQuery = query(activityQuery, where('entityType', '==', filters.entityType));
    }

    if (filters.userId) {
      activityQuery = query(activityQuery, where('userId', '==', filters.userId));
    }

    activityQuery = query(activityQuery, limit(filters.limit || 100));

    const snapshot = await getDocs(activityQuery);
    return entriesFromSnapshot(snapshot as ActivityLogSnapshot);
  }
}
